import React, { ReactElement, useMemo } from "react";
import { WindowGridContainer } from "../components/WindowGridContainer";
import { ResponsiveLine, Serie, PointTooltipProps } from "@nivo/line";
import { Grid, Typography, Box, styled, useTheme } from "@mui/material";
import { PlayerEstimate } from "./EstimationResult";
import { calculateAlignmentScore } from "../utils/alignmentScore";
import { alignmentScoreToText, getAlignmentScoreColor, ALIGNMENT_SCORE_THRESHOLDS } from "../utils/alignmentScoreUtils";

const ChartContainer = styled(Box)({
    height: '250px',
    width: '100%',
});

// Custom layer to show alignment score threshold lines
const CustomLayerWithScoreIntervals = ({ yScale }: { yScale: (value: number) => number }) => {
    return (
        <>
            {ALIGNMENT_SCORE_THRESHOLDS.map((value, index) => {
                const nextValue = ALIGNMENT_SCORE_THRESHOLDS[index + 1];
                if (nextValue !== undefined && nextValue !== 100) {
                    return (
                        <line
                            key={value}
                            x1={0}
                            x2="100%"
                            y1={yScale(value)}
                            y2={yScale(value)}
                            stroke={getAlignmentScoreColor(value)}
                            strokeWidth={1}
                            strokeDasharray="2 2"
                            opacity={0.3}
                        />
                    );
                }
                return null;
            })}
            {/* Perfect line at 100 */}
            <line
                key="perfect"
                x1={0}
                x2="100%"
                y1={yScale(100)}
                y2={yScale(100)}
                stroke={getAlignmentScoreColor(100)}
                strokeWidth={1}
                strokeDasharray="2 2"
                opacity={0.3}
            />
        </>
    );
};

interface ReEstimationInsightsProps {
    allRounds?: PlayerEstimate[][];
    currentEstimates?: PlayerEstimate[];
}

export default function ReEstimationInsights(props: ReEstimationInsightsProps): ReactElement {
    const theme = useTheme();
    const textColor = theme.palette.text.primary;

    // Calculate alignment scores for all rounds
    const chartData = useMemo(() => {
        if (!props.allRounds || props.allRounds.length === 0 || !props.currentEstimates) {
            return null;
        }
        
        const allRoundsData = [...props.allRounds, props.currentEstimates];
        const dataPoints = allRoundsData.map((roundEstimates, index) => {
            const score = calculateAlignmentScore(roundEstimates);
            return {
                x: index + 1, // Round number
                y: score >= 0 ? score : 0 // Use 0 for invalid scores
            };
        });
        
        return [{
            id: 'alignment',
            data: dataPoints
        }] as Serie[];
    }, [props.allRounds, props.currentEstimates]);

    if (!chartData || chartData[0].data.length === 0) {
        return <></>;
    }

    const dataPoints = chartData[0].data;
    const latestScore = dataPoints[dataPoints.length - 1]?.y as number;
    const previousScore = dataPoints.length > 1 ? dataPoints[dataPoints.length - 2]?.y as number : null;
    const scoreChange = previousScore !== null ? latestScore - previousScore : null;
    const isImproving = scoreChange !== null && scoreChange > 0;
    const isDeclining = scoreChange !== null && scoreChange < 0;

    return (
        <WindowGridContainer title="Re-estimation Progress">
            <Grid size={12} sx={{ pl: 1 }}>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                                Current Round
                            </Typography>
                            <Typography variant="h6">
                                Round {dataPoints.length}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Alignment Score
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        color: latestScore >= 0 ? getAlignmentScoreColor(latestScore) : 'text.primary',
                                        lineHeight: 1.2
                                    }}
                                >
                                    {latestScore >= 0 ? latestScore : 'N/A'}
                                </Typography>
                                {scoreChange !== null && (
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            color: isImproving ? 'success.main' : isDeclining ? 'error.main' : 'text.secondary',
                                            fontWeight: 'bold',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {isImproving ? '↑' : isDeclining ? '↓' : '→'} {Math.abs(scoreChange)} points
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <ChartContainer>
                    <ResponsiveLine
                        data={chartData}
                        margin={{ top: 20, right: 20, bottom: 40, left: 40 }}
                        xScale={{ type: 'linear', min: 0.5, max: dataPoints.length + 0.5 }}
                        yScale={{ type: 'linear', min: 0, max: 100 }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Round',
                            legendPosition: 'middle',
                            legendOffset: 36,
                            tickValues: dataPoints.map((_, i) => i + 1)
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Score',
                            legendPosition: 'middle',
                            legendOffset: -30,
                            tickValues: [0, 25, 50, 75, 100]
                        }}
                        pointSize={8}
                        pointColor={{ from: 'color', modifiers: [] }}
                        pointBorderWidth={2}
                        pointBorderColor={{ from: 'serieColor' }}
                        pointLabelYOffset={-12}
                        useMesh={true}
                        colors={theme.palette.secondary.main}
                        lineWidth={2}
                        curve="monotoneX"
                        enableGridX={true}
                        enableGridY={true}
                        gridYValues={ALIGNMENT_SCORE_THRESHOLDS}
                        layers={[
                            "grid",
                            "axes",
                            "areas",
                            "lines",
                            "points",
                            "slices",
                            "mesh",
                            "legends",
                            CustomLayerWithScoreIntervals,
                        ]}
                        tooltip={(({ point }: { point: { data: { x: number; y: number } } }) => {
                            const score = point.data.y as number;
                            return (
                                <div style={{
                                    background: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: '4px',
                                    padding: '8px',
                                    boxShadow: theme.shadows[3],
                                    color: theme.palette.text.primary
                                }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                        Round {point.data.x}
                                    </div>
                                    <div style={{ marginBottom: '4px' }}>
                                        Score: {score}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px',
                                        color: getAlignmentScoreColor(score)
                                    }}>
                                        {alignmentScoreToText(score)}
                                    </div>
                                </div>
                            );
                        }) as React.FC<PointTooltipProps>}
                        theme={{
                            axis: {
                                ticks: {
                                    text: {
                                        fill: textColor,
                                        fontSize: 11,
                                    },
                                },
                                legend: {
                                    text: {
                                        fill: textColor,
                                        fontSize: 11,
                                    },
                                },
                            },
                            grid: {
                                line: {
                                    stroke: theme.palette.divider,
                                    strokeWidth: 1,
                                },
                            },
                        }}
                    />
                </ChartContainer>
            </Grid>
        </WindowGridContainer>
    );
}

